import { call, put, select } from 'redux-saga/effects'
import { utils, writeFileXLSX } from 'xlsx'
import dayjs from 'dayjs'
import { IApiResponse } from 'shared/constants'
import { Action } from 'infra/types'
import { FETCH_ACCOUNTS_SUMMARY, IAccountsState } from 'state/new-summary/accounts/accounts.types'
import {
  fetchAccountSummary,
  fetchSpecificAccountSummary
} from 'services/Api/StaffLinkApiFolder/api'
import {
  setAccountSpecificGroupDetails,
  setAccountSpecificGroupSummary,
  setAccountSummaryLoader,
  setAccountsSummaryFilter,
  setOverallGroupsStats,
  setOverallStats
} from 'state/new-summary/accounts/accounts.action'
import {
  FETCH_GROUP_ACCOUNTS_SUMMARY_RESPONSE,
  FETCH_OVERALL_ACCOUNTS_SUMMARY_RESPONSE
} from 'services/Api/StaffLinkApiFolder/type'
import { IAppState } from 'infra/AppState'
import { IDashboardState } from 'state/dashboard/dashboard.types'

export function* fetchAccountSummaryEffect(action: Action<FETCH_ACCOUNTS_SUMMARY>) {
  yield put(setAccountSummaryLoader({ loaderName: 'fetchingOverallSummary', status: true }))
  const { startTime, endTime, filterType } = action.payload
  const requestPayload = {
    start_date: startTime,
    end_date: endTime
  }
  const response: IApiResponse<FETCH_OVERALL_ACCOUNTS_SUMMARY_RESPONSE> = yield call(
    fetchAccountSummary,
    requestPayload
  )

  yield put(
    setOverallStats({
      groupId: 'overall',
      groupName: 'Overall',
      credit: Number(response.data.overall_stats.total_credit_amount) / 100,
      payment: Number(response.data.overall_stats.total_payment_amount) / 100
    })
  )
  yield put(
    setOverallGroupsStats(
      response.data.overall_group_stats.map((group) => {
        return {
          groupId: group.group_id,
          groupName: group.group_name,
          credit: Number(group.total_credit_amount) / 100,
          payment: Number(group.total_payment_amount) / 100
        }
      })
    )
  )
  yield put(
    setAccountsSummaryFilter({
      startTime,
      endTime,
      filterType
    })
  )
  yield put(setAccountSummaryLoader({ loaderName: 'fetchingOverallSummary', status: false }))
}

export function* fetchSpecificAccountSummaryEffect(
  action: Action<FETCH_ACCOUNTS_SUMMARY & { groupId: string }>
) {
  yield put(setAccountSummaryLoader({ loaderName: 'fetchingSpecificSummary', status: true }))
  const { startTime, endTime, filterType, groupId } = action.payload
  const requestPayload = {
    group_id: groupId,
    start_date: startTime,
    end_date: endTime
  }
  const response: IApiResponse<FETCH_GROUP_ACCOUNTS_SUMMARY_RESPONSE> = yield call(
    fetchSpecificAccountSummary,
    requestPayload
  )

  if (response.data.overall) {
    yield put(
      setAccountSpecificGroupSummary({
        credit: Number(response.data.overall.total_credit_amount) / 100,
        payment: Number(response.data.overall.total_payment_amount) / 100
      })
    )
  }

  const { customers }: IDashboardState = yield select((app: IAppState) => app.Dashboard)
  const formattedData = response.data.transactions.map((transaction) => {
    return {
      transactionTime: Number(transaction.transaction_time),
      customerId: transaction.account_id,
      customerName: customers.byIds[transaction.account_id].description,
      note: transaction.note,
      credit: transaction.is_credit ? Number(transaction.amount) / 100 : 0,
      payment: !transaction.is_credit ? Number(transaction.amount) / 100 : 0
    }
  })
  yield put(setAccountSpecificGroupDetails(formattedData))
  yield put(
    setAccountsSummaryFilter({
      startTime,
      endTime,
      filterType
    })
  )
  yield put(setAccountSummaryLoader({ loaderName: 'fetchingSpecificSummary', status: false }))
}

export function* downloadAccountSummaryEffect() {
  yield put(setAccountSummaryLoader({ loaderName: 'downloadingOverallSummary', status: true }))

  const { filter }: IAccountsState = yield select((app: IAppState) => app.NewSummary.AccountSummary)
  const response: IApiResponse<FETCH_OVERALL_ACCOUNTS_SUMMARY_RESPONSE> = yield call(
    fetchAccountSummary,
    {
      start_date: filter?.startTime || 0,
      end_date: filter?.endTime || 0
    }
  )

  const data = [
    {
      'Group Name': 'Overall',
      Credit: Number(response.data.overall_stats.total_credit_amount) / 100,
      Payment: Number(response.data.overall_stats.total_payment_amount) / 100
    }
  ]
  response.data.overall_group_stats.forEach((gp) => {
    data.push({
      'Group Name': gp.group_name,
      Credit: Number(gp.total_credit_amount) / 100,
      Payment: Number(gp.total_payment_amount) / 100
    })
  })
  const ws = utils.json_to_sheet(data, { origin: 'A8' })
  const excelHeading = 'Accounts Summary'
  utils.sheet_add_aoa(
    ws,
    [
      [excelHeading],
      [],
      ['Statement Created On:', dayjs.unix(dayjs().unix()).format('DD MMM, YYYY')],
      []
    ],
    {
      origin: 'A1'
    }
  )
  const wb = utils.book_new()
  utils.book_append_sheet(wb, ws, 'Sheet 1')
  writeFileXLSX(wb, 'AccountsGroupSummary.xlsx')

  yield put(setAccountSummaryLoader({ loaderName: 'downloadingOverallSummary', status: false }))
}

export function* downloadGroupAccountSummaryEffect(
  action: Action<{ groupId: string; groupName: string }>
) {
  yield put(setAccountSummaryLoader({ loaderName: 'downloadingOverallSummary', status: true }))
  const { groupName, groupId } = action.payload
  const { filter }: IAccountsState = yield select((app: IAppState) => app.NewSummary.AccountSummary)
  const response: IApiResponse<FETCH_GROUP_ACCOUNTS_SUMMARY_RESPONSE> = yield call(
    fetchSpecificAccountSummary,
    {
      start_date: filter?.startTime || 0,
      end_date: filter?.endTime || 0,
      group_id: groupId === 'overall' ? '' : groupId
    }
  )

  const { customers }: IDashboardState = yield select((app: IAppState) => app.Dashboard)
  const data: any = []
  response.data.transactions.forEach((txn) => {
    data.push({
      'Group Name': action.payload.groupId === 'overall' ? 'Overall' : groupName,
      'Customer Name': customers.byIds[txn.account_id].description,
      'Transaction Time': dayjs.unix(Number(txn.transaction_time)).format('DD MMM, YYYY'),
      Credit: txn.is_credit ? Number(txn.amount) / 100 : 0,
      Payment: !txn.is_credit ? Number(txn.amount) / 100 : 0
    })
  })
  const ws = utils.json_to_sheet(data, { origin: 'A8' })
  const excelHeading = `${groupName} - Accounts Summary`
  utils.sheet_add_aoa(
    ws,
    [
      [excelHeading],
      [],
      ['Statement Created On:', dayjs.unix(dayjs().unix()).format('DD MMM, YYYY')],
      []
    ],
    {
      origin: 'A1'
    }
  )
  const wb = utils.book_new()
  utils.book_append_sheet(wb, ws, 'Sheet 1')
  writeFileXLSX(wb, 'AccountsGroupSummary.xlsx')

  yield put(setAccountSummaryLoader({ loaderName: 'downloadingOverallSummary', status: false }))
}
